import React, { useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V2/Button/Button';
import { TextField2 } from 'components/UI';
import { MarketplacePetTag, MarketplaceClientTag } from '@spike/marketplace-model';
import { TaggedContext } from '@spike/marketplace-action';
import { DetailTable } from './DetailTable';
import { TagUsage } from './TagsUsage/TagUsage';
import SearchName from './SearchName';
import styles from './TagTab.module.scss';

interface TagTabProps {
    tabId?: string;
    loading: boolean;
    showForm: boolean;
    newTagName: string;
    editingTag: string | null;
    personalities: Array<MarketplacePetTag | MarketplaceClientTag>;
    entityType?: 'pet' | 'client';
    context: TaggedContext;
    setNewTagName: (name: string) => void;
    openModalDelete: (petTag: MarketplacePetTag | MarketplaceClientTag) => void;
    addPetTagHandle?: (name: string, editingTag: string | null) => void;
    editPetTagHandle?: (petTag: MarketplacePetTag | MarketplaceClientTag) => void;
    addOtherHandle?: (showForm: boolean) => void;
    addCancelHandle?: () => void;
}

export const TagTab: React.FC<TagTabProps> = ({
    loading,
    showForm,
    newTagName,
    editingTag,
    personalities,
    entityType,
    context,
    setNewTagName,
    openModalDelete,
    addPetTagHandle,
    editPetTagHandle,
    addOtherHandle,
    addCancelHandle
}) => {
    const [searchText, setSearchText] = useState('');
    const [showSearch] = useState(false);
    const [tagUsageTag, setTagUsageTag] = useState<MarketplacePetTag | MarketplaceClientTag | null>(null);

    const filteredTags = personalities.filter(tag => tag.name.toLowerCase().includes(searchText.toLowerCase()));

    const handleAddOther = () => {
        addOtherHandle?.(true);
    };

    const handleAddTag = () => {
        addPetTagHandle?.(newTagName, editingTag);
    };

    const handleEditTag = (tag: MarketplacePetTag | MarketplaceClientTag) => {
        editPetTagHandle?.(tag);
    };

    const handleCancel = () => {
        addCancelHandle?.();
    };

    return (
        <>
            <SearchName
                searchText={searchText}
                showSearch={showSearch}
                onSearchChange={setSearchText}
            />
            <DetailTable
                personalities={filteredTags}
                loading={loading}
                entityType={entityType}
                openModalDelete={openModalDelete}
                editPetTagHandle={handleEditTag}
                onCountClick={setTagUsageTag}
            />
            {showForm ? (
                <div className={styles.formContainer}>
                    <TextField2
                        id="business_settings_pet_tags_input_pet_tag_name"
                        className={styles.tagNameField}
                        name="name"
                        placeholder="Tag"
                        value={newTagName}
                        onChange={setNewTagName}
                        required={true}
                    />
                    <Button
                        label={editingTag ? 'Update' : 'Save'}
                        variant="green"
                        size="medium"
                        onClick={handleAddTag}
                        className={styles.saveButton}
                    />
                    <FontAwesomeIcon
                        id="business_settings_pet_tags_icon_delete"
                        icon={faXmark}
                        className={clsx(styles.trashIcon, loading && styles.disabled)}
                        onClick={handleCancel}
                    />
                </div>
            ) : (
                <div className={styles.addOtherContainer}>
                    <p
                        id="business_settings_pet_tags_button_add_other"
                        className={clsx(styles.addOther, loading && styles.disabled)}
                        onClick={handleAddOther}
                    >
                        + Add other
                    </p>
                </div>
            )}
            {tagUsageTag && (
                <TagUsage
                    open={true}
                    tag={tagUsageTag}
                    entityType={entityType ?? 'pet'}
                    context={context}
                    onClose={() => setTagUsageTag(null)}
                />
            )}
        </>
    );
};

export default TagTab;
